<template>
  <AttrGroup title="应用与数据项">
    <AttrSelect
      v-if="canUse('jump-application').valid"
      label="跳转应用"
      :value="jumpAppOption"
      class="w-full"
      :options="jumpAppOptions"
      :disabled="!!attrDisabled || isSlotFollowing"
      :search="true"
      @change="(val: string) => onChange(val, 'jumpApp')"
    />

    <div
      v-if="
        jumpAppOption === 'customName' &&
        canUse('jump-application').valid &&
        canUse('jump-application-custom-name').valid
      "
      class="mt-2.5"
    >
      <div class="flex items-center">
        <div
          class="w-full rounded-lg flex flex-col flex-grow justify-between items-center bg-black-card relative p-2.5 border border-transparent border-solid transition-all"
          :class="{
            'select-none pointer-events-none':
              !!attrDisabled || isSlotFollowing,
            '!border-[#838383]': customNameHover || customNameFocused,
            '!border-red-600': customNameError.show
          }"
          @mousemove="() => (customNameHover = true)"
          @mouseleave="() => (customNameHover = false)"
        >
          <div class="w-full flex" @keydown.stop>
            <span
              class="flex-shrink-0 mr-2.5 leading-tight text-[14px] relative"
              :class="{
                'opacity-30': !!attrDisabled || isSlotFollowing
              }"
            >
              自定义名称

              <a-tooltip
                v-if="!attrDisabled"
                title="支持输入字符、数字、特殊字符仅支持'.'"
              >
                <icon
                  class="cursor-default absolute -top-1 -right-4"
                  icon="help.svg"
                  :width="14"
                  :height="14"
                  fill="none"
                />
              </a-tooltip>
            </span>
            <a-input
              v-model:value="customNameValue"
              class="w-full ml-8 flex-grow bg-[rgb(49,49,49)] outline-0 border-none p-0 leading-tigh text-right"
              :disabled="!!attrDisabled || isSlotFollowing"
              placeholder="请输入自定义名称"
              @focus="customNameFocused = true"
              @blur="customNameFocused = false"
              @input="validateCustomName"
            />
          </div>
        </div>
      </div>
      <div v-if="customNameError.show" class="text-red-600 mt-[10px]">
        {{ customNameError.msg }}
      </div>
    </div>
    <AttrSelect
      v-if="canUse('associated-data-item').valid"
      label="关联数据项"
      :value="shape.attrs.relatedDataKey ?? 'none'"
      class="w-full mt-2.5"
      :options="relatedDataOptions"
      :disabled="!!attrDisabled || isSlotFollowing"
      :search="true"
      @change="(val: string) => onChange(val, 'relatedDataKey')"
    />

    <AttrSwitch
      v-if="canUse('min-click-zone').valid && jumpAppOption !== 'none'"
      class="mt-2.5"
      label="点击最小区域展示"
      :value="shape.attrs.minClickZone"
      :disabled="!!attrDisabled || isSlotFollowing"
      @change="(val:boolean) => onChange(val, 'minClickZone')"
    />
    <AttrSelect
      v-if="jumpAppOption === 'alipay' && canUse('alipay-content').valid"
      label="支付内容"
      :value="alipayContentOption"
      class="w-full mt-2.5"
      :options="alipayContentOptions"
      :disabled="!!attrDisabled || isSlotFollowing"
      @change="(val: string) => onChange(val, 'jumpAppAlipayContent')"
    />
  </AttrGroup>
  <template
    v-if="
      jumpAppOption === 'sport' &&
      ['N62S', 'O61', 'P62', 'P67', 'Q61', 'Q63', 'Q66'].indexOf(_deviceType) !== -1
    "
  >
    <AttrGroup title="选择运动类型">
      <AttrSelect
        label="运动类型"
        :value="sportTypeOption"
        class="w-full"
        :options="sportTypeOptions"
        :disabled="!!attrDisabled || isSlotFollowing"
        @change="(val: number) => onChange(val, 'jumpAppSportTypeId')"
      />
      <AttrSelect
        label="运动名称"
        :value="sportOption"
        class="w-full mt-2.5"
        :options="sportOptions"
        :disabled="!!attrDisabled || isSlotFollowing"
        :search="true"
        @change="(val: any) => onChange(val, 'jumpAppSportId')"
      />
    </AttrGroup>
  </template>
</template>
<script lang="ts" setup>
import { getAvailableJumpAppList, JumpAppItem } from '@/hooks';
import { useSlotFollow } from '@/hooks/use-slot-follow';
import { jumpSportList } from '@/resources/jump-sport-list';
import { jumpSportListO61 } from '@/resources/jump-sport-list-O61';
import { getDeviceTranslations } from '@/resources/widget-translations';
import { Shape, ShapeType } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { computed, PropType, ref, watch } from 'vue';
const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});
const watchfaceStore = useWatchfaceStore();
const editorStore = useEditorStore();
const { judgeIfFollowingSlot, followParentAttrs } = useSlotFollow();
const attrDisabled = computed(() => editorStore.attrDisabled);

const _deviceType = computed(() => watchfaceStore.deviceType);

const jumpAppOptions = computed(() => {
  const options: JumpAppItem[] = getAvailableJumpAppList(_deviceType.value);

  return options.map((i) => ({ value: i.name, label: i.label }));
});
const jumpAppOption = computed(() => {
  let _jumpAppoption = '';
  if (!props.shape.attrs.jumpApp) _jumpAppoption = 'none';
  else _jumpAppoption = props.shape.attrs.jumpApp;
  return _jumpAppoption;
});

const onChange = (val: string | number | boolean, attr: string) => {
  props.shape.setAttrs({ [attr]: val });
  if (
    props.shape.parent &&
    props.shape.parent.attrs.followingGroupName !== undefined
  ) {
    const widgetIndex = props.shape.parent.children.findIndex(
      (item) => item.id === props.shape.id
    );
    followParentAttrs(
      props.shape.parent,
      attr,
      val,
      ShapeType.Widget,
      widgetIndex
    );
  }
};

const relatedDataOptions = computed(() => {
  return getDeviceTranslations(_deviceType.value);
});

const sportTypeOption = computed(() => {
  let _sportTypeOption;
  if (props.shape.attrs.jumpAppSportTypeId === undefined) _sportTypeOption = -1;
  else _sportTypeOption = props.shape.attrs.jumpAppSportTypeId;
  return _sportTypeOption;
});

const sportTypeOptions = computed(() => {
  const sportList =
    _deviceType.value === 'N62S'
      ? jumpSportList
      : jumpSportListO61;
  return sportList.map((i) => ({ value: i.sportTypeId, label: i.label }));
});

const sportOption = computed(() => {
  let _sportOption;
  if (props.shape.attrs.jumpAppSportId === undefined) _sportOption = -1;
  else _sportOption = props.shape.attrs.jumpAppSportId;
  return _sportOption;
});

const sportOptions = computed(() => {
  const sportList =
    _deviceType.value === 'N62S'
      ? jumpSportList
      : jumpSportListO61;
  // O61、Q61 设备不支持力量训练（sportId: 308）
  const excludeSportIds = ['Q61'].includes(_deviceType.value) ? [308] : [];
  return (
    sportList
      .find((item) => item.sportTypeId === sportTypeOption.value)
      ?.children
      .filter((item) => !excludeSportIds.includes(item.sportId))
      .map((item) => ({ value: item.sportId, label: item.label })) ??
    []
  );
});
const isSlotFollowing = computed(() => {
  return judgeIfFollowingSlot(props.shape.attrs.slotFollowingId ?? undefined);
});

// 支付宝支付内容相关逻辑
const alipayContentOptions = [
  { value: 'openApp', label: '打开应用' },
  { value: 'paymentCode', label: '付款码' },
  { value: 'tapPay', label: '碰一下' }
];
const alipayContentOption = computed(() => {
  return props.shape.attrs.jumpAppAlipayContent ?? 'openApp';
});

// 自定义名称相关逻辑
const customNameValue = ref('');
const customNameFocused = ref(false);
const customNameHover = ref(false);
const customNameError = ref({
  show: false,
  msg: ''
});

// 自定义名称输入验证（必须在 watch 之前定义）
const validateCustomName = () => {
  const value = customNameValue.value;
  const validPattern = /^[a-zA-Z0-9.\u4e00-\u9fff]*$/;

  if (!validPattern.test(value)) {
    customNameError.value = {
      show: true,
      msg: "仅支持输入字符、数字、特殊字符仅支持'.'"
    };
  } else {
    customNameError.value = {
      show: false,
      msg: ''
    };
  }
};

// 监听shape属性变化，同步自定义名称值
watch(
  () => props.shape.attrs.jumpAppCustomName,
  (newValue) => {
    customNameValue.value = newValue || '';
    // 切换图层时，如果是自定义名称模式，需要重新验证
    if (jumpAppOption.value === 'customName') {
      validateCustomName();
    }
  },
  { immediate: true }
);

// 监听自定义名称值变化，同步到shape属性
watch(
  () => customNameValue.value,
  (newValue) => {
    if (jumpAppOption.value === 'customName') {
      onChange(newValue, 'jumpAppCustomName');
    }
  }
);

// 当跳转应用改变时，清理自定义名称错误状态
watch(
  () => jumpAppOption.value,
  (newValue) => {
    if (newValue !== 'customName') {
      customNameError.value = {
        show: false,
        msg: ''
      };
      customNameValue.value = '';
      props.shape.setAttrs({ jumpAppCustomName: undefined });
    } else {
      // 切换到自定义名称时，恢复之前的值并重新验证
      customNameValue.value = props.shape.attrs.jumpAppCustomName || '';
      // 恢复值后需要重新验证，以显示错误提示
      validateCustomName();
    }
    // 切换跳转应用时，清理支付宝支付内容
    if (newValue !== 'alipay') {
      props.shape.setAttrs({ jumpAppAlipayContent: undefined });
    }
  }
);
</script>
